import React from 'react';

import { gql } from '@apollo/client';
import { CandidateFilledIcon, ModernLoopLogoIcon, SparkleIcon } from '@modernloop/shared/icons';
import { getPrimaryAccentBackground, getPrimaryAccentContrastText } from '@modernloop/shared/theme';
import { Avatar, Box, Theme } from '@mui/material';

import { ActorType, ResponseStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import { useIsFirstInAgentRun } from 'src/entities/ActivityLog/ActivityLogListItemContext';
import { ActivityLogActorEmployeeLike, ActivityLogDetailsLike } from 'src/entities/ActivityLog/types';
import AvatarWithRsvp from 'src/entities/common/AvatarWithRsvp';
import IconWithRsvp from 'src/entities/common/IconWithRsvp';

interface Props {
  actorName: string;
  responseStatus: ResponseStatus | null | undefined;
}

type ActivityLogAvatarLike = {
  id: string;
  activityActorType: ActorType;
  actorEmployee?: ActivityLogActorEmployeeLike | null;
  details?: ActivityLogDetailsLike | null;
};

interface Fragments {
  activityLog: ActivityLogAvatarLike;
}

const getAgentVisual = (detailsTypename?: string | null): { emoji: string; key: string } => {
  switch (detailsTypename) {
    case 'ActivityLogAgentReasoningDetails':
      return { emoji: '🤔', key: 'reasoning' };
    case 'ActivityLogAgentToolCallsDetails':
    case 'ActivityLogAgentToolCallsStartDetails':
    case 'ActivityLogAgentToolCallsContinueDetails':
      return { emoji: '🛠️', key: 'tool-calls' };
    case 'ActivityLogAgentMessageDetails':
      return { emoji: '💬', key: 'message' };
    case 'ActivityLogAgentQuestionDetails':
      return { emoji: '❓', key: 'question' };
    case 'ActivityLogAgentDebugDetails':
      return { emoji: '🧪', key: 'debug' };
    case 'ActivityLogAgentQueuedDetails':
      return { emoji: '⏳', key: 'queued' };
    case 'ActivityLogEmailDetails':
    case 'ActivityLogEmployeeEmailSentDetails':
    case 'ActivityLogEmployeeEmailReceivedDetails':
      return { emoji: '✉️', key: 'email' };
    default:
      return { emoji: '🤖', key: 'agent' };
  }
};

const ActivityLogAvatar: FCWithFragments<Fragments, Props> = ({ activityLog, actorName, responseStatus }) => {
  const { activityActorType, actorEmployee } = activityLog;
  const isFirstInAgentRun = useIsFirstInAgentRun();

  switch (activityActorType) {
    case ActorType.Employee:
      return (
        <AvatarWithRsvp
          size={20}
          dataTestId={`activity-log-base-avatar-rsvp-${actorName}-${responseStatus}`}
          alt={actorName}
          responseStatus={responseStatus}
          src={actorEmployee?.internalProfilePictureUrl ?? actorEmployee?.fullName ?? undefined}
        />
      );
    case ActorType.System:
      return <ModernLoopLogoIcon data-testid={`activity-log-base-avatar-rsvp-${actorName}`} />;
    case ActorType.Candidate:
      return (
        <IconWithRsvp
          icon={<CandidateFilledIcon data-testid="activity-log-base-candidate-icon-filled" />}
          responseStatus={responseStatus}
          interviewerRsvpIconDataTestId={`activity-log-base-candidate-rsvp-icon-${responseStatus}`}
        />
      );
    case ActorType.Agent: {
      // eslint-disable-next-line no-underscore-dangle
      const agentVisual = getAgentVisual(activityLog.details?.__typename);
      if (!isFirstInAgentRun) {
        return (
          <Box
            component="span"
            data-testid={`activity-log-agent-emoji-${agentVisual.key}`}
            sx={{
              width: 20,
              height: 20,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            {agentVisual.emoji}
          </Box>
        );
      }
      return (
        <Avatar
          data-testid="activity-log-agent-sparkle-avatar"
          sx={{ width: 20, height: 20, background: (theme: Theme) => getPrimaryAccentBackground(theme) }}
        >
          <SparkleIcon
            data-testid="activity-log-agent-sparkle-icon"
            sx={{ fontSize: 12, color: (theme: Theme) => getPrimaryAccentContrastText(theme) }}
          />
        </Avatar>
      );
    }
    default:
      return null;
  }
};

ActivityLogAvatar.fragments = {
  activityLog: gql`
    fragment ActivityLogAvatar_activityLog on ActivityLog {
      id
      activityActorType
      actorEmployee {
        id
        fullName
        internalProfilePictureUrl
      }
      details {
        __typename
      }
    }
  `,
};

export default ActivityLogAvatar;
